/**
 * routine-labels.ts
 *
 * Helpers de presentación para rutinas.
 *
 * Estados posibles (alineados con el backend RoutineEffectiveStatus):
 *   'effective'  → Vigente   (isActive && hoy dentro del período)
 *   'scheduled'  → Programada (isActive && startDate aún no llegó)
 *   'ended'      → Finalizada (isActive && endDate ya pasó)
 *   'disabled'   → Deshabilitada (isActive = false)
 */

import { getTyrisIcon } from '../components/ui/tyris-icon';

export type RoutineEffectiveStatus = 'effective' | 'scheduled' | 'ended' | 'disabled';

export interface RoutineBadge {
  className: string;
  label: string;
}

/** Mapa de estado → presentación visual */
const STATUS_MAP: Record<RoutineEffectiveStatus, RoutineBadge> = {
  effective: { className: 'badge-routine badge-routine-effective', label: `${getTyrisIcon('racha', { size: 14 })} Vigente` },
  scheduled: { className: 'badge-routine badge-routine-scheduled', label: `${getTyrisIcon('asistencia', { size: 14 })} Programada` },
  ended:     { className: 'badge-routine badge-routine-ended',     label: `${getTyrisIcon('éxito', { size: 14 })} Finalizada` },
  disabled:  { className: 'badge-routine badge-routine-disabled',  label: `${getTyrisIcon('descanso', { size: 14 })} Deshabilitada` },
};

/**
 * Calcula el estado efectivo de una rutina en el cliente.
 * Acepta el campo `effectiveStatus` del servidor si está disponible,
 * o lo recalcula localmente a partir de isActive + startDate + endDate.
 */
export function getRoutineEffectiveStatus(routine: {
  isActive: boolean;
  startDate: string;
  endDate?: string | null;
  effectiveStatus?: RoutineEffectiveStatus;
}): RoutineEffectiveStatus {
  // Si el servidor ya lo calculó, lo usamos directamente
  if (routine.effectiveStatus) return routine.effectiveStatus;

  // Cálculo local (fallback)
  if (!routine.isActive) return 'disabled';

  const now = new Date();
  // Comparación day-level en UTC
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const start = new Date(routine.startDate);
  const startDay = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());

  if (today < startDay) return 'scheduled';

  if (routine.endDate) {
    const end = new Date(routine.endDate);
    const endDay = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
    if (today > endDay) return 'ended';
  }

  return 'effective';
}

/**
 * Devuelve el badge { className, label } para una rutina.
 * Compatible con el uso existente en admin-routine.ts y admin-routines.ts.
 */
export function routineStatusBadge(routine: {
  isActive: boolean;
  startDate: string;
  endDate?: string | null;
  effectiveStatus?: RoutineEffectiveStatus;
}): RoutineBadge {
  const status = getRoutineEffectiveStatus(routine);
  return STATUS_MAP[status];
}

/**
 * @deprecated Usar routineStatusBadge(routine) en su lugar.
 * Mantenida por compatibilidad hasta actualizar todas las páginas.
 */
export function routineStatusBadgeFromBool(isActive: boolean): RoutineBadge {
  return isActive
    ? STATUS_MAP.effective
    : STATUS_MAP.disabled;
}

export function formatRoutineDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toISOString().split('T')[0];
}
